import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { findUser } from '../actions/userActions';
import { CART } from '../constants/routes';
import { convertSecondsToMilliseconds, isAppPWA } from '../utils/helper-functions';
import useAxios from '../axios/useAxios';

import { useScanVerification } from './scanCheck/hooks/useScanVerification';
import { useManualVerification } from './scanCheck/hooks/useManualVerification';

import SuccessNotif from '../components/SuccessNotif';
import ScannerContainer from './scanCheck/ScannerContainer';
import ManualVerificationOverlay from './scanCheck/ManualVerificationOverlay';
import PrimaryButton from '../components/button/PrimaryButton';

import { checkStateCartItems } from '../utils/cartItem.js';
import { useFunnelTracking } from '../posthog/funnelTracking';
import { useUserLoginStatus } from '../utils/hooks';
import { usePerformanceContext } from '../context/PerformanceContext.jsx';

const ScanCheck = () => {
  const { cartItems } = useSelector((state) => state.cart);
  const { store } = useSelector((state) => state.store);
  const user = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const axiosInstance = useAxios();
  const isPWA = isAppPWA();
  const CHECKOUT_TYPE_MANUAL = 'manual';

  const isNotProd = process.env.NODE_ENV !== 'production' || process.env.REACT_APP_TOOLKIT?.toUpperCase() === 'TRUE';

  const [explicationcheck, setExplicationCheck] = useState(true);
  const [checkoutQrScanned, setCheckoutQrScanned] = useState(false);

  const NEXT_ROUTE = store?.permissions?.pikkoJackpot
    ? `/jackpot/${cartItems?._id}`
    : `/receipt/${cartItems?._id}`;

  const { markComplete } = usePerformanceContext();

  const {
    setCode,
    isDestroy,
    isScanditLoaded,
  } = useScanVerification(cartItems, user, axiosInstance);

  const {
    manualVerification,
    setManualVerification,
    checkIfOrderHasBeenManualyVerified,
  } = useManualVerification(cartItems, axiosInstance);
  const isLoggedIn = useUserLoginStatus();
  const { trackPageViewScanCheck, trackFunnelOrderPaid } = useFunnelTracking();

  useEffect(() => {
    trackPageViewScanCheck({});
    trackFunnelOrderPaid({
                           transaction_id: cartItems?._id,
                           order_value: cartItems?.promoPrice || cartItems?.itemsPrice || 0,
                           items_purchased: cartItems?.orderItems?.length || 0,
                           payment_method: cartItems?.paymentMethod,
                           user_loggedIn: isLoggedIn,
                         });
  }, [trackPageViewScanCheck]);

  function closeAnimManualVerification() {
    if (cartItems) {
      const orderId = cartItems?._id;

      setManualVerification(false);
      navigate(NEXT_ROUTE);
    } else {
      console.error('There is no cartItem to redirect to receipt screen');
      navigate('/');
    }
  }

  useEffect(() => {
    window.scrollTo(0, 0);
    if (user?._id) {
      dispatch(findUser(user, axiosInstance, navigate));
    }

    const scanditVideo = document.querySelector('.scandit .scandit-video');
    if (scanditVideo) scanditVideo.style.height = '30vh';
  }, []);

  useEffect(() => {
    let intervalManualVerification;
    let timeoutAnimManualverification;

    const orderId = cartItems?._id;
    checkStateCartItems(cartItems, navigate, dispatch, `/receipt/${orderId}`);

    if (cartItems) {
      if (!cartItems?.manualVerification) {
        if (!intervalManualVerification) {
          intervalManualVerification = setInterval(checkIfOrderHasBeenManualyVerified, 4000);
        }
      }

      if (cartItems?.verification) {
        const orderId = cartItems._id;
        setCheckoutQrScanned(true);

        if (store?.checkoutVerificationType === CHECKOUT_TYPE_MANUAL) {
          setManualVerification(true);
          let timeoutAnimManualverification = convertSecondsToMilliseconds(store?.checkoutVerificationTimeout || 30);
          timeoutAnimManualverification = setTimeout(() => {
            closeAnimManualVerification();
          }, timeoutAnimManualverification);
        } else {
        navigate(NEXT_ROUTE);
        }
      }

      if (!cartItems?.isPaid) {
        navigate(CART);
      }

      if (cartItems?.manualVerification) {
        setCheckoutQrScanned(true);

        if (timeoutAnimManualverification) {
          clearTimeout(timeoutAnimManualverification);
        }
        closeAnimManualVerification();
      }
    }

    return () => {
      if (intervalManualVerification) {
        clearInterval(intervalManualVerification);
      }
      if (timeoutAnimManualverification) {
        clearTimeout(timeoutAnimManualverification);
      }
    };
  }, [cartItems]);


  const fakeScan = (storeId) => {
    if (!storeId || !isNotProd) return;
    setCode(`${window.location.origin}/?verif=${storeId}`);
  };
  


  useEffect(() => {
    if (cartItems?.isPaid && isScanditLoaded && !isDestroy &&
      !manualVerification && axiosInstance && !user.userLoading) {
      markComplete();
    }
  }, [cartItems, isScanditLoaded, isDestroy, manualVerification, axiosInstance, user.userLoading, markComplete]);

  if (!cartItems) return null;

  return (
    <>
      {!explicationcheck && (
        <SuccessNotif actioner={() => {
          setExplicationCheck(true);
        }} />
      )}

      {!checkoutQrScanned && (
        <>
          <ScannerContainer
            isPWA={isPWA}
            isScanditLoaded={isScanditLoaded}
            isDestroy={isDestroy}
            onScan={(scanResult) => setCode(scanResult.barcodes[0].data)}
            onExplanationClick={() => setExplicationCheck(false)}
          />
          {isNotProd && (
            <PrimaryButton
              label="[DEV] Fake Scan"
              onClick={() => fakeScan(cartItems?.storeId)}
              className="!fixed bottom-[120px] left-1/2 w-[70dvw] -translate-x-1/2 z-[999]"
            />
          )}
        </>
      )}

      {checkoutQrScanned && manualVerification && (
        <ManualVerificationOverlay />
      )}
    </>
  );
};

export default ScanCheck;
